import type { ConvertedPiece, Tone } from "@kensio/pinyinjs";
import { joinPieces } from "@kensio/pinyinjs";

import {
  clipboardDocument,
  type CopyFlavours,
  escapeHtmlLines,
} from "./clipboard.js";
import { inkColour } from "./tone-colour.js";

/**
 * What the copy buttons put on the clipboard.
 *
 * The half that ends up in a Word document, which is why nothing here may
 * depend on the site's stylesheet: each syllable carries its own `color`,
 * taken from the palette through {@link inkColour}, and the colours are the
 * light scheme's whatever scheme the visitor is reading in.
 *
 * It is built from the same pieces the screen is, in `output.ts`, which is why
 * the two cannot drift apart.
 */

/**
 * A syllable and the text it was written as, for the pages that start from
 * something already romanised rather than from a conversion.
 */
export interface WrittenSyllable {
  readonly text: string;
  readonly tone: Tone | undefined;
}

/** One syllable as markup, coloured if the palette has a colour for its tone. */
function coloured(
  text: string,
  tone: Tone | undefined,
  reference: Element,
): string {
  const escaped = escapeHtmlLines(text);
  const colour = inkColour(tone, reference);

  return colour === undefined
    ? escaped
    : `<span style="color:${colour}">${escaped}</span>`;
}

/**
 * A conversion as markup carrying literal colours.
 *
 * `reference` is any element in the document; it is what the custom properties
 * are resolved against.
 */
export function outputHtml(
  pieces: readonly ConvertedPiece[],
  reference: Element,
): string {
  return clipboardDocument(
    pieces
      .map((piece) => coloured(piece.text, piece.syllable?.tone, reference))
      .join(""),
  );
}

/**
 * Everything the copy button puts on the clipboard, for a conversion.
 */
export function copyFlavoursOf(
  pieces: readonly ConvertedPiece[],
  reference: Element,
): CopyFlavours {
  return {
    text: joinPieces(pieces),
    html: outputHtml(pieces, reference),
  };
}

/**
 * The same, for a run of syllables that were read rather than converted.
 */
export function writtenFlavours(
  written: readonly WrittenSyllable[],
  reference: Element,
): CopyFlavours {
  return {
    text: written.map((each) => each.text).join(""),
    html: clipboardDocument(
      written.map((each) => coloured(each.text, each.tone, reference)).join(""),
    ),
  };
}

/**
 * The clipboard flavours for a plain string, for the panels that have no
 * syllables to colour — a bopomofo transcription, a Chinese numeral.
 */
export function plainFlavours(text: string): CopyFlavours {
  return {
    text,
    html: clipboardDocument(escapeHtmlLines(text)),
  };
}
